import moment from 'moment';

import {
  EMPTY_MEDICAL_INFO_VALUES,
  MEDICAL_INFO_DATE_FIELD_NAMES,
  MEDICAL_INFO_FIELD_NAMES,
  MEDICAL_INFO_NUMERIC_FIELD_NAMES,
} from '../constants';
import { getMedicalInfoSchema } from '../validationSchema';
import { API_DATE_FORMAT } from '../../../../../../../constants';

import type {
  IIntakeMedicalInfo,
  IntakeMedicalInfoPayload,
} from '../../../../../../../store/questionnaires/intake';
import type { MedicalFormValues } from '../types';
import type {
  MedicalInfoFieldName,
  MedicalInfoFieldValue,
  MedicalInfoSavedFieldValues,
} from './types';

function isDateFieldName(name: MedicalInfoFieldName): boolean {
  return MEDICAL_INFO_DATE_FIELD_NAMES.some((fieldName) => fieldName === name);
}

function isNumericFieldName(name: MedicalInfoFieldName): boolean {
  return MEDICAL_INFO_NUMERIC_FIELD_NAMES.some((fieldName) => fieldName === name);
}

function hasSavedMedicalInfoScalar(medicalInfo: IIntakeMedicalInfo): boolean {
  return MEDICAL_INFO_FIELD_NAMES.some((name) => {
    const value = medicalInfo[name];
    return value != null && value !== '';
  });
}

export function getMedicalInfoInitialValues(
  medicalInfo: IIntakeMedicalInfo | null | undefined,
): MedicalFormValues {
  // List-only creates insert an empty medical_info stub so the new row has a parent. Treat that
  // the same as a missing record so Formik's enableReinitialize does not reset in-progress edits.
  if (medicalInfo == null || !hasSavedMedicalInfoScalar(medicalInfo)) {
    return EMPTY_MEDICAL_INFO_VALUES;
  }

  const toText = (value: string | null): string => value ?? '';
  const toNumberText = (value: number | null): string => (value != null ? String(value) : '');

  return {
    allergies: toText(medicalInfo.allergies),
    special_diets: toText(medicalInfo.special_diets),
    hearing_difficulty: toText(medicalInfo.hearing_difficulty),
    hearing_screening_date: medicalInfo.hearing_screening_date
      ? moment(medicalInfo.hearing_screening_date)
      : null,
    vision_difficulty: toText(medicalInfo.vision_difficulty),
    vision_screening_date: medicalInfo.vision_screening_date
      ? moment(medicalInfo.vision_screening_date)
      : null,
    trauma_history: toText(medicalInfo.trauma_history),
    hospitalization_history: toText(medicalInfo.hospitalization_history),
    f84_severity_level: toText(medicalInfo.f84_severity_level),
    f84_diagnosis_provider_name: toText(medicalInfo.f84_diagnosis_provider_name),
    f84_diagnosis_city: toText(medicalInfo.f84_diagnosis_city),
    f84_diagnosis_state: toText(medicalInfo.f84_diagnosis_state),
    f84_diagnosis_year: toNumberText(medicalInfo.f84_diagnosis_year),
    child_delivery_type: toText(medicalInfo.child_delivery_type),
    child_gestational_age: toNumberText(medicalInfo.child_gestational_age),
    child_weight_at_birth_pounds: toNumberText(medicalInfo.child_weight_at_birth_pounds),
    child_weight_at_birth_ounces: toNumberText(medicalInfo.child_weight_at_birth_ounces),
    child_sitting_up_age_months: toNumberText(medicalInfo.child_sitting_up_age_months),
    child_crawling_age_months: toNumberText(medicalInfo.child_crawling_age_months),
    child_walking_age_years: toNumberText(medicalInfo.child_walking_age_years),
    child_walking_age_months: toNumberText(medicalInfo.child_walking_age_months),
    child_standing_age_years: toNumberText(medicalInfo.child_standing_age_years),
    child_standing_age_months: toNumberText(medicalInfo.child_standing_age_months),
    child_babbling_age_months: toNumberText(medicalInfo.child_babbling_age_months),
    child_first_word: toText(medicalInfo.child_first_word),
    child_second_word: toText(medicalInfo.child_second_word),
    child_first_words_age_months: toNumberText(medicalInfo.child_first_words_age_months),
  };
}

export function getSavedFieldValue(name: MedicalInfoFieldName, values: MedicalFormValues): string {
  if (isDateFieldName(name)) {
    const dateValue = values[name];
    return moment.isMoment(dateValue) ? dateValue.format(API_DATE_FORMAT) : '';
  }
  const fieldValue = values[name];
  return fieldValue == null ? '' : String(fieldValue);
}

export function getSavedFieldValues(values: MedicalFormValues): MedicalInfoSavedFieldValues {
  return MEDICAL_INFO_FIELD_NAMES.reduce<MedicalInfoSavedFieldValues>(
    (saved, name) => ({ ...saved, [name]: getSavedFieldValue(name, values) }),
    {},
  );
}

export function getChangedSavedFieldValues(
  values: MedicalFormValues,
  savedValues: MedicalInfoSavedFieldValues,
): MedicalInfoSavedFieldValues {
  return MEDICAL_INFO_FIELD_NAMES.reduce<MedicalInfoSavedFieldValues>((changed, name) => {
    const savedValue = getSavedFieldValue(name, values);
    if (savedValues[name] === savedValue) {
      return changed;
    }
    return { ...changed, [name]: savedValue };
  }, {});
}

export function getChangedFieldNames(
  changedSavedValues: MedicalInfoSavedFieldValues,
): MedicalInfoFieldName[] {
  return MEDICAL_INFO_FIELD_NAMES.filter((name) => name in changedSavedValues);
}

export function getPatchData(
  name: MedicalInfoFieldName,
  values: MedicalFormValues,
): IntakeMedicalInfoPayload {
  if (isDateFieldName(name)) {
    const dateValue = values[name];
    return {
      [name]: moment.isMoment(dateValue) ? dateValue.format(API_DATE_FORMAT) : null,
    };
  }
  if (isNumericFieldName(name)) {
    const numericValue = values[name];
    return { [name]: numericValue === '' ? null : Number(numericValue) };
  }
  const fieldValue = values[name];
  return { [name]: fieldValue === '' ? null : fieldValue };
}

export function getMergedPatchData(
  values: MedicalFormValues,
  names: MedicalInfoFieldName[],
): IntakeMedicalInfoPayload {
  return names.reduce<IntakeMedicalInfoPayload>(
    (data, name) => ({ ...data, ...getPatchData(name, values) }),
    {},
  );
}

export function getValuesWithFieldOverride(
  values: MedicalFormValues,
  name: MedicalInfoFieldName,
  value: MedicalInfoFieldValue | undefined,
): MedicalFormValues {
  if (value === undefined) {
    return values;
  }
  return { ...values, [name]: value };
}

export function isDateFieldValid(name: MedicalInfoFieldName, values: MedicalFormValues): boolean {
  if (!isDateFieldName(name)) {
    return true;
  }
  const dateValue = values[name];
  return dateValue == null || (moment.isMoment(dateValue) && dateValue.isValid());
}

export async function isFieldSchemaValid(
  name: MedicalInfoFieldName,
  values: MedicalFormValues,
  areScreeningDatesRequired: boolean,
): Promise<boolean> {
  try {
    await getMedicalInfoSchema(areScreeningDatesRequired).validateAt(name, values);
    return true;
  } catch {
    return false;
  }
}
